import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQueryClient } from 'react-query'
import { rolesAPI } from '../../services/api'
import productRolesAPI from '../../services/productRolesAPI'
import Card, { CardHeader, CardTitle, CardContent } from '../UI/Card'
import Button from '../UI/Button'
import LoadingSpinner from '../UI/LoadingSpinner'
import { X, Shield } from 'lucide-react'
import toast from 'react-hot-toast'

export default function EditRoleModal({ isOpen, onClose, role, productId }) {
  const queryClient = useQueryClient()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [availablePermissions, setAvailablePermissions] = useState({})
  const [features, setFeatures] = useState([])

  const { register, handleSubmit, reset, formState: { errors }, watch } = useForm({
    defaultValues: {
      name: '',
      description: '',
      permissions: {}
    }
  })

  // Reset form when role changes
  useEffect(() => {
    if (role && isOpen) {
      reset({
        name: role.name || '',
        description: role.description || '',
        permissions: role.permissions || {}
      })
    }
  }, [role, isOpen, reset])

  // Fetch available permissions when modal opens
  useEffect(() => {
    if (isOpen && productId) {
      productRolesAPI.getAvailablePermissions(productId)
        .then(response => {
          const permissions = response.data.permissions || {}
          setAvailablePermissions(permissions)
          
          // Extract features from permissions
          const featureList = Object.entries(permissions.features || {}).map(([id, feature]) => ({
            id,
            name: feature.name,
            description: feature.description,
            module_name: feature.module_name
          }))
          setFeatures(featureList)
        })
        .catch(error => {
          console.error('Error fetching permissions:', error)
        })
    }
  }, [isOpen, productId])

  const mutation = useMutation((roleData) => rolesAPI.update(role.id, roleData), {
    onSuccess: () => {
      queryClient.invalidateQueries(['product-roles', productId])
      queryClient.invalidateQueries(['product', productId])
      queryClient.invalidateQueries(['roles'])
      toast.success('Role updated successfully!')
      onClose()
    },
    onError: (error) => {
      console.error('Error updating role:', error)
      toast.error(error.message || 'Failed to update role')
    }
  })

  const onSubmit = async (data) => {
    if (!role) return

    setIsSubmitting(true)
    try {
      const roleData = {
        name: data.name,
        description: data.description,
        permissions: data.permissions
      }
      console.log('Updating role with data:', roleData)
      await mutation.mutateAsync(roleData)
    } catch (error) {
      // Error handled in mutation onError
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    if (!isSubmitting) {
      reset()
      onClose()
    }
  }

  const handlePermissionChange = (category, permission, checked) => {
    const currentPermissions = watch('permissions') || {}
    const updatedPermissions = {
      ...currentPermissions,
      [category]: {
        ...currentPermissions[category],
        [permission]: checked
      }
    }
    reset({
      ...watch(),
      permissions: updatedPermissions
    })
  }

  const handleFeaturePermissionChange = (featureId, checked) => {
    const currentPermissions = watch('permissions') || {}
    const updatedPermissions = {
      ...currentPermissions,
      features: {
        ...currentPermissions.features,
        [featureId]: checked
      }
    }
    reset({
      ...watch(),
      permissions: updatedPermissions
    })
  }

  if (!isOpen || !role) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div className="flex items-center space-x-2">
            <div className="h-8 w-8 rounded-lg bg-blue-100 flex items-center justify-center">
              <Shield className="h-4 w-4 text-blue-600" />
            </div>
            <CardTitle>Edit Feature Role: {role.name}</CardTitle>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Role Name */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Role Name *
              </label>
              <input
                {...register('name', { 
                  required: 'Role name is required',
                  minLength: { value: 2, message: 'Name must be at least 2 characters' }
                })}
                type="text"
                className="input w-full"
                placeholder="Enter role name"
                disabled={isSubmitting}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Description
              </label>
              <textarea
                {...register('description')}
                rows={3}
                className="input w-full resize-none"
                placeholder="Enter role description"
                disabled={isSubmitting}
              />
            </div>

            {/* Feature Permissions */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-4">
                Feature Access Permissions
              </label>
              
              {features.length > 0 ? (
                <div className="space-y-4">
                  {/* Individual Features */}
                  <div className="border border-secondary-200 rounded-lg p-4">
                    <h4 className="font-medium text-secondary-900 mb-3">Product Features</h4>
                    <div className="space-y-3">
                      {features.map((feature) => (
                        <label key={feature.id} className="flex items-start space-x-3">
                          <input
                            type="checkbox"
                            checked={watch(`permissions.features.${feature.id}`) || false}
                            onChange={(e) => handleFeaturePermissionChange(feature.id, e.target.checked)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-1"
                            disabled={isSubmitting}
                          />
                          <div className="flex-1">
                            <span className="text-sm font-medium text-secondary-900">{feature.name}</span>
                            {feature.description && (
                              <p className="text-xs text-secondary-600 mt-1">{feature.description}</p>
                            )}
                            {feature.module_name && (
                              <span className="inline-block text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded mt-1">
                                {feature.module_name}
                              </span>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                </div>
              ) : (
                <div className="text-center py-8 border border-secondary-200 rounded-lg">
                  <div className="text-secondary-400 mb-2">
                    <svg className="h-12 w-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-secondary-900 mb-2">No Features Available</h3>
                  <p className="text-secondary-600 mb-4">Create features in the Features tab first, then assign permissions to them here.</p>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-secondary-200">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="min-w-[100px]"
              >
                {isSubmitting ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  'Update Role'
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
